// read-aloud-chrome — the "Read aloud" control on the isolated editor surfaces
// (read-aloud-instructions).
//
// Wired UNIFORMLY from main.js, not dispatched by data-surface: it applies to every surface
// that renders instruction prose, and per-surface dispatch would mean twelve near-identical
// entries. It wires EAGERLY, next to wireSaveShortcut / applyStoredConsoleHeight — above the
// crossOriginIsolated early-return and outside loadProject().then() — see wireReadAloud below
// for why that matters.
//
// It shares ONE engine with the classroom island: `src/islands/shared/read-aloud.js`. Both
// trees are entries of the same Vite `rollupOptions.input`, so the cross-tree import resolves
// at build time into a shared chunk named `read-aloud` (named explicitly in vite.config.mjs,
// because chunkFileNames routes it into the un-hashed js/shell/ directory).
//
// It never touches editor-core or editor-api: the instruction HTML is SERVER-RENDERED and the
// preference arrives as <body> data attributes, so nothing here needs the project payload.
//
// THREE TARGETS, all server-rendered markdown, and all three id-less-or-not variants matter
// (the ids the originating card named — #instructionsOutput and #challenge-instructions — are
// both dead in this codebase):
//   #instructions-banner-body   the shared collapsible banner (11 surfaces + the in-app pgzero
//                               editor); the control goes in the banner HEADER as a sibling of
//                               .instructions-banner-toggle, never nested inside it (nested
//                               buttons are invalid and the click would collapse the banner).
//   .instructions-pane          the pgzero editor's narrow left aside.
//   .tweak-intro                the tweak surface's slim intro strip.
// On the two id-less targets the container IS the extraction root, so the control is inserted
// as a PRECEDING SIBLING — never inside, or extraction would read the button's own label and
// the voice would open by announcing "Read aloud".

import {
  play,
  stop,
  hasSpeech,
  speechAvailable,
  onSpeechAvailable
} from '../../../src/islands/shared/read-aloud.js';

// Hand-rolled Lucide glyphs, following the DW_CHECK_SVG / DW_CHEV_SVG precedent in
// challenge-chrome.js — the isolated surfaces cannot use <pc-icon>. Same two icons the
// classroom renders through pc-icon: 'volume-2' idle, 'square' while speaking.
var SVG_OPEN = '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">';
var VOLUME_SVG = SVG_OPEN + '<path d="M11 4.702a.705.705 0 0 0-1.203-.498L6.413 7.587A1.4 1.4 0 0 1 5.416 8H3a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h2.416a1.4 1.4 0 0 1 .997.413l3.383 3.384A.705.705 0 0 0 11 19.298z"/><path d="M16 9a5 5 0 0 1 0 6"/><path d="M19.364 18.364a9 9 0 0 0 0-12.728"/></svg>';
var STOP_SVG = SVG_OPEN + '<rect width="18" height="18" x="3" y="3" rx="2"/></svg>';

// Every control this module created, so a late `speechAvailable` flip can add them and a late
// flip back can remove them.
var mounted = [];
var subscribed = false;

function prefs(doc) {
  var body = doc && doc.body;
  if (!body || !body.getAttribute) { return null; }
  var flag = body.getAttribute('data-read-aloud');
  // ABSENT means anonymous — the templates guard all three attributes on `{% if user %}`,
  // and an anonymous viewer gets no control anywhere. The value is a STRING ('on' / 'off'),
  // so it is compared as one: `!== false` would be true for 'off', 'on' and null alike.
  if (flag !== 'on') { return null; }
  return {
    rate: body.getAttribute('data-read-aloud-rate') || '',
    // An empty stored voice means "browser default"; a stored name matching no live voice
    // also falls back silently, and is never rewritten (the engine owns that rule).
    voice: body.getAttribute('data-read-aloud-voice') || ''
  };
}

function setState(button, playing) {
  button.innerHTML = (playing ? STOP_SVG : VOLUME_SVG) + '<span>' + (playing ? 'Stop' : 'Read aloud') + '</span>';
  button.setAttribute('aria-label', playing ? 'Stop reading' : 'Read aloud');
  button.setAttribute('aria-pressed', playing ? 'true' : 'false');
}

// One control. `roots` is the ordered array the engine reads; `tint` is the caller-nominated
// "now reading" container (the engine never infers it); `onBeforePlay` lets the banner expand
// itself before speaking.
function makeControl(doc, options) {
  var button = doc.createElement('button');
  button.type = 'button';
  button.className = 'pcra-btn';
  setState(button, false);
  button.addEventListener('click', function () {
    if (button.getAttribute('aria-pressed') === 'true') { stop(); return; }
    if (typeof options.onBeforePlay === 'function') {
      try { options.onBeforePlay(); } catch (e) { /* never block speech on chrome */ }
    }
    play({
      roots: options.roots(),
      tint: options.tint,
      rate: options.rate,
      voice: options.voice,
      onStateChange: function (playing) { setState(button, playing); }
    });
  });
  return button;
}

function remember(button) {
  mounted.push(button);
}

function removeAll() {
  stop();
  mounted.forEach(function (button) {
    try { if (button.parentNode) { button.parentNode.removeChild(button); } } catch (e) { /* gone already */ }
  });
  mounted = [];
}

// --- the three targets --------------------------------------------------------

function mountBanner(doc, pref) {
  var body = doc.getElementById('instructions-banner-body');
  if (!body) { return; }
  var banner = doc.getElementById('instructions-banner') || body.parentNode;
  var toggle = doc.getElementById('instructions-banner-toggle');
  if (!banner || !toggle) { return; }
  // An empty-state banner ("No instructions have been added yet.", which the challenge
  // surface always renders) extracts to nothing, so it gets NO control — not a disabled one.
  if (!hasSpeech([body])) { return; }

  var button = makeControl(doc, {
    roots: function () { return [body]; },
    tint: body,
    rate: pref.rate,
    voice: pref.voice,
    // Painting a highlight inside a display:none region is pointless, and the highlight is
    // the whole point — so a collapsed banner expands first, then speaks.
    onBeforePlay: function () {
      if (banner.classList.contains('is-collapsed')) {
        banner.classList.remove('is-collapsed');
        toggle.setAttribute('aria-expanded', 'true');
      }
    }
  });
  button.classList.add('pcra-btn-banner');
  banner.classList.add('pcra-has-control');
  // A SIBLING of the toggle, inserted after it — never a child of it.
  if (toggle.nextSibling) { banner.insertBefore(button, toggle.nextSibling); }
  else { banner.appendChild(button); }
  remember(button);
}

function mountBefore(doc, container, pref, className) {
  if (!container || !container.parentNode) { return; }
  if (!hasSpeech([container])) { return; }
  var button = makeControl(doc, {
    roots: function () { return [container]; },
    tint: container,
    rate: pref.rate,
    voice: pref.voice
  });
  button.classList.add(className);
  container.classList.add('pcra-has-control');
  // PRECEDING SIBLING: these containers ARE the extraction root, so the control can never
  // live inside them.
  container.parentNode.insertBefore(button, container);
  remember(button);
}

function mountAll(doc, pref) {
  mountBanner(doc, pref);
  mountBefore(doc, doc.querySelector('.instructions-pane'), pref, 'pcra-btn-pane');
  mountBefore(doc, doc.querySelector('.tweak-intro'), pref, 'pcra-btn-intro');
}

/**
 * EAGER entry point, wired from main.js above the crossOriginIsolated early-return and outside
 * loadProject().then(). Late wiring would silently ship no control on `.tweak-intro` and
 * `.instructions-pane` (neither is in the `body.no-isolation` hide list that makes late wiring
 * harmless for the banner), and would ship no control on EVERY surface whenever loadProject
 * rejects. Null-safe and throw-safe: it runs inside main.js's IIFE, where a throw would skip
 * every later wiring step.
 */
export function wireReadAloud(doc) {
  try {
    doc = doc || (typeof document !== 'undefined' ? document : null);
    if (!doc || !doc.body) { return; }

    var pref = prefs(doc);
    if (!pref) { return; }

    if (speechAvailable()) { mountAll(doc, pref); }

    // getVoices() is asynchronous and typically empty on the first call, so the control may
    // only become legitimate after first paint — and on a voiceless profile (some managed
    // Chromebooks) it must never appear at all.
    if (!subscribed) {
      subscribed = true;
      onSpeechAvailable(function (available) {
        try {
          if (available && !mounted.length) { mountAll(doc, pref); }
          else if (!available) { removeAll(); }
        } catch (e) { console.error(e); }
      });
    }
  } catch (e) {
    console.error(e);
  }
}
